import { useActor, useInterpret } from '@xstate/react';
import { useAtom } from 'jotai';
import { isEqual } from 'lodash';
import * as React from 'react';
import { InterpreterFrom } from 'xstate';

import { bootstrapConfig } from '~src/context/config';
import { useGlobalState } from '~src/global-state';
import { useIpc } from '~src/utils/ipc-utils';

import { useBillingAddressMutation } from '../billing-address/billing-address-mutation';
import {
  BillingFlowMachine,
  createBillingFlowMachine,
} from '../state-machines/billing-flow-machine';
import { isSameAsDeliveryCheckboxCheckedAtom } from './state-store';

const BillingFormFlowContext = React.createContext<
  | {
      billingFlowService: InterpreterFrom<BillingFlowMachine>;
      editInitialAddress: boolean;
      initialAddressEntry: boolean;
    }
  | undefined
>(undefined);

export const BillingFormFlowProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const ipc = useIpc();
  const billingMutation = useBillingAddressMutation();
  const globalState = useGlobalState();
  const [, setIsSameAsRecipient] = useAtom(isSameAsDeliveryCheckboxCheckedAtom);

  const initialBillingEntry = !!bootstrapConfig.externalAddressBookProvider
    ? !!globalState.address_book?.addresses && globalState.address_book?.addresses.length <= 1
    : !globalState.billing_address && !bootstrapConfig.userId;

  const editInitialBilling =
    !!globalState.billing_address &&
    !bootstrapConfig.userId &&
    !bootstrapConfig.externalAddressBookProvider;

  const [billingFlowMachine] = React.useState(() =>
    createBillingFlowMachine({
      initialEntry: initialBillingEntry,
      isBillingPresent: !!globalState.billing_address, //todo
      hideAddressBook: globalState.address_form_configuration?.billing_features?.hide_address_book,
      addressBookLength: globalState.address_book?.addresses?.length,
      externalAddressBookProvider: bootstrapConfig.externalAddressBookProvider,
      extendedValidationFlow:
        globalState.address_form_configuration?.global_features?.enable_extended_validation,
    })
  );

  const billingFlowService = useInterpret(
    billingFlowMachine,
    {
      services: {
        updateBillingService: async (ctx, event) => {
          const res = {
            addressBookLength: ctx.addressBookLength,
          };

          if (event.data) {
            const onError = 'onError' in event.data ? event.data.onError : undefined;

            await billingMutation.mutateAsync(event.data.address, {
              onSuccess: stateResponse => {
                if (!stateResponse) {
                  return;
                }

                if (stateResponse.address_book?.addresses) {
                  res.addressBookLength = stateResponse.address_book.addresses.length;
                }
              },
              onError,
            });
          }

          return res;
        },
      },
      guards: {
        isBillingTheSame: (_, e) => isEqual(globalState.billing_address, e.data.address), // no field in billing has been changed between edit and submission
        editInitialAddress: () => editInitialBilling,
        isSameAsRecipient: ctx => globalState.recipient?.id === ctx.currentAddress?.id,
      },
    },
    state => {
      ipc.send({
        name: 'sync_billing_fsm_state',
        data: {
          context: state.context,
          meta: {
            fetching: state.hasTag('fetching'),
          },
        },
      });
    }
  );

  React.useState(() => billingFlowService.start());

  const [state, send] = useActor(billingFlowService);

  React.useEffect(() => {
    if (globalState.address_book?.addresses?.length) {
      send({
        type: 'UPDATE_COUNT',
        data: { addressBookLength: globalState.address_book.addresses.length },
      });
    }

    if (!globalState.billing_address) {
      send({ type: 'BILLING_SAME_AS_RECIPIENT' });
    }
  }, [globalState]);

  React.useEffect(() => {
    if (
      !!bootstrapConfig.externalAddressBookProvider &&
      (['checkout.billingSummary', 'modal.modalAddressForm'].some(state.matches) ||
        (state.matches('checkout.inlineAddressForm') &&
          state.matches('checkbox.unchecked') &&
          !!state.context.currentAddress))
    ) {
      setIsSameAsRecipient(false);
    } else {
      setIsSameAsRecipient(true);
    }
  }, [state, send]);

  return (
    <BillingFormFlowContext.Provider
      value={{
        billingFlowService,
        editInitialAddress: editInitialBilling,
        initialAddressEntry: initialBillingEntry,
      }}
    >
      {children}
    </BillingFormFlowContext.Provider>
  );
};

BillingFormFlowContext.displayName = 'BillingFormFlowProvider';

export function useBillingFormFlow() {
  const state = React.useContext(BillingFormFlowContext);
  if (state !== undefined) {
    return state;
  }
  throw 'BillingFormFlowContext is not yet defined, you are probably using it in a wrong place';
}
